import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import { PublishIcon } from '@blocksuite/icons/rc';
import { type FC, useId } from 'react';

import * as styles from '../automation-settings-panel.css';

/**
 * Whether the automation can be shared in the Hub now, and if not, why:
 * - `ready`: sharing opens the automation under "Mine" in the Hub, where the
 *   user decides between the workspace and a public submission.
 * - `needs-run`: a template needs one successful run as its proof.
 * - `no-role`: only workspace owners and admins share.
 * - `not-in-plan`: the plan does not include publishing.
 */
export type HubShareState = 'ready' | 'needs-run' | 'no-role' | 'not-in-plan';

/** Why sharing is unavailable, shown below the button. */
const REASON_KEYS: Record<Exclude<HubShareState, 'ready'>, string> = {
  'needs-run': 'com.streamlain.automation.panel.hub.needs-run',
  'no-role': 'com.streamlain.automation.panel.hub.no-role',
  'not-in-plan': 'com.streamlain.automation.panel.hub.not-in-plan',
};

/** The Hub state in words, for the sidebar card. */
export const hubShareSummaryKey = (state: HubShareState) =>
  state === 'ready'
    ? 'com.streamlain.automation.panel.hub.summary.ready'
    : state === 'needs-run'
      ? 'com.streamlain.automation.panel.hub.summary.needs-run'
      : 'com.streamlain.automation.panel.hub.summary.unavailable';

export const HubShareSection: FC<{
  state: HubShareState;
  onShare: () => void;
}> = ({ state, onShare }) => {
  const t = useI18n();
  const titleId = useId();
  const reasonId = useId();
  const reasonKey = state === 'ready' ? null : REASON_KEYS[state];

  // Same layout as every setting: the name on top, then the action, then at
  // most one hint below it (why sharing is not possible yet).
  return (
    <section
      className={styles.settingSection}
      data-setting="hub"
      aria-labelledby={titleId}
    >
      <div className={styles.settingHeader}>
        <div className={styles.settingHeaderText}>
          <h4 id={titleId} className={styles.settingTitle}>
            {t['com.streamlain.automation.panel.hub.title']()}
          </h4>
        </div>
      </div>
      <Button
        variant="secondary"
        className={styles.settingAction}
        prefix={<PublishIcon />}
        disabled={state !== 'ready'}
        aria-describedby={reasonKey ? reasonId : undefined}
        onClick={onShare}
        data-testid="hub-share-button"
      >
        {t['com.streamlain.marketplace.publish.title.new']()}
      </Button>
      {reasonKey && (
        <p
          id={reasonId}
          className={styles.settingHint}
          data-testid="hub-share-reason"
        >
          {t.t(reasonKey)}
        </p>
      )}
    </section>
  );
};
